import { Brands } from 'src/utils/enums/brands.enum';
import { IUser } from 'src/utils/interfaces/user';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import { isBrandAudi, isBrandDucati, isSiteCanadian } from 'src/utils/helpers';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import React, { Dispatch, SetStateAction } from 'react';

interface IWarningNotificationProps {
    type: NotificationType;
    visibility: boolean;
    setVisibility: Dispatch<SetStateAction<boolean>>;
    warningMessageKey: string;
}

enum WarningMessageKeyType {
    QUOTE_CANNOT_BE_RETURNED = 'QuoteCannotBeReturned',
    CLOSED_ACCOUNT = 'PQClosedAccountMessage',
    NOT_IN_PORTFOLIO = 'NotInYourPortfolio',
    REDIRECT_VW = 'PayoffQuoteOpenLaneMessageVW',
    REDIRECT_AUDI = 'PayoffQuoteOpenLaneMessageAudi',
    QUOTE_EXCEPTION = 'ExceptionPayoffQuoteOriginalMaturityDate',
    UNKNOWN_EXCEPTION_ACC = 'ExceptionUnknownAccount',
    PAYOFF_EXCLUDED = 'PQAccountQuoteBlocked',
    CHARGED_OFF_US_AUDI = 'PayoffQuoteUSAChargedOffErrorMessageAudi',
    CHARGED_OFF_US_VW = 'PayoffQuoteUSAChargedOffErrorMessageVW',
    CHARGED_OFF_US_DUCATI = 'PayoffQuoteUSAChargedOffErrorMessageDucati',
    CONTACT_REP_AUDI = 'PQContactRepAudi',
    CONTACT_REP_VW = 'PQContactRepVW',
    CONTACT_REP_DUCATI = 'PQContactRepDucati',
}

export function getWarningMessageKey(err: any) {
    let key = '';
    if (err?.response?.data) {
        const { error } = err.response.data;

        if (error?.hasWarningMessage) {
            key = error.warningMessageKey;
        }
    }

    return key;
}

const WarningNotification: React.FC<IWarningNotificationProps> = ({
    type,
    visibility,
    setVisibility,
    warningMessageKey,
}) => {
    const { warningMessages, labelText } = useLocalization();
    const brand = useSelector((state: IUser) => {
        return state.dealerDataInfo?.dealer.brand ?? 'VW';
    });

    function getCompanyAndPhone() {
        let company = '';
        let phone = '';
        const isCanadian = isSiteCanadian();
        if (isBrandAudi(brand)) {
            company = labelText.LABEL_TEXT_BRAND_AUDI_FINANCIAL_SERVICES;
            phone = isCanadian ? '1 (888) 412-2834' : '888-AFS-AUDI (237-2834)';
        } else if (isBrandDucati(brand)) {
            company = labelText.LABEL_TEXT_BRAND_DUCATI_FINANCIAL_SERVICES;
            phone = isCanadian ? '855-938-2284' : '855-938-2284';
        } else {
            company = labelText.LABEL_TEXT_BRAND_VOLKSWAGEN_FINANCIAL_SERVICES;
            phone = isCanadian ? '1 (800) 668-8224' : '800-428-4034';
        }

        return { '%b': company, '%p': phone };
    }

    function getQuoteCannotBeReturnedMessage() {
        const replacements: Record<string, string> = getCompanyAndPhone();
        const msg = isSiteCanadian()
            ? warningMessages.QUOTE_CANNOT_BE_RETURNED_CA
            : warningMessages.QUOTE_CANNOT_BE_RETURNED_US;

        return msg.replace(/%b|%p/g, (match) => replacements[match] || match);
    }

    const setUnknownException = () => {
        let message = '';
        if (isSiteCanadian()) {
            if (brand === Brands.VW || brand === Brands.Volkswagen)
                message = warningMessages.UNKNOWN_EXCEPTION_VW_CA;
            else if (brand === Brands.AUDI)
                message = warningMessages.UNKNOWN_EXCEPTION_AUDI_CA;
            else if (brand === Brands.DUCATI)
                message = warningMessages.UNKNOWN_EXCEPTION_DUCATI_CA;
        } else {
            if (brand === Brands.VW || brand === Brands.Volkswagen)
                message = warningMessages.UNKNOWN_EXCEPTION_VW_US;
            else if (brand === Brands.AUDI)
                message = warningMessages.UNKNOWN_EXCEPTION_AUDI_US;
            else if (brand === Brands.DUCATI)
                message = warningMessages.UNKNOWN_EXCEPTION_DUCATI_US;
        }
        return message;
    };

    function getPayoffExcludedMessage() {
        let message = '';
        if (isBrandAudi(brand) || isBrandDucati(brand)) {
            message = warningMessages.PAYOFF_PAYMENT_EXCLUDED_AUDI;
        } else {
            message = warningMessages.PAYOFF_PAYMENT_EXCLUDED_VW;
        }
        return message;
    }

    let message = '';
    switch (warningMessageKey) {
        case WarningMessageKeyType.QUOTE_CANNOT_BE_RETURNED:
            message = getQuoteCannotBeReturnedMessage();
            break;
        case WarningMessageKeyType.CLOSED_ACCOUNT:
            message = warningMessages.CLOSED_ACCOUNT;
            break;
        case WarningMessageKeyType.NOT_IN_PORTFOLIO:
            message = warningMessages.NOT_IN_PORTFOLIO;
            break;
        case WarningMessageKeyType.REDIRECT_VW:
            message = isSiteCanadian()
                ? warningMessages.REDIRECT_CA
                : warningMessages.REDIRECT_US_VW;
            break;
        case WarningMessageKeyType.REDIRECT_AUDI:
            message = isSiteCanadian()
                ? warningMessages.REDIRECT_CA
                : warningMessages.REDIRECT_US_AUDI;
            break;
        case WarningMessageKeyType.QUOTE_EXCEPTION:
            message = isSiteCanadian()
                ? warningMessages.QUOTE_EXCEPTION_CA
                : warningMessages.QUOTE_EXCEPTION_US;
            break;
        case WarningMessageKeyType.UNKNOWN_EXCEPTION_ACC:
            message = setUnknownException();
            break;
        case WarningMessageKeyType.PAYOFF_EXCLUDED:
            message = getPayoffExcludedMessage();
            break;
        case WarningMessageKeyType.CHARGED_OFF_US_AUDI:
            message = warningMessages.CHARGED_OFF_US_AUDI;
            break;
        case WarningMessageKeyType.CHARGED_OFF_US_VW:
            message = warningMessages.CHARGED_OFF_US_VW;
            break;
        case WarningMessageKeyType.CHARGED_OFF_US_DUCATI:
            message = warningMessages.CHARGED_OFF_US_DUCATI;
            break;
        case WarningMessageKeyType.CONTACT_REP_AUDI:
            message = warningMessages.CONTACT_REP_AUDI;
            break;
        case WarningMessageKeyType.CONTACT_REP_VW:
            message = warningMessages.CONTACT_REP_VW;
            break;
        case WarningMessageKeyType.CONTACT_REP_DUCATI:
            message = warningMessages.CONTACT_REP_DUCATI;
            break;
        default:
            message = setUnknownException();
            break;
    }

    return (
        <Notification
            content=''
            contentHtml={message}
            type={type}
            visibility={visibility}
            setVisibility={setVisibility}
        />
    );
};

export default WarningNotification;
